import { useEffect, useMemo, useState } from 'react';
import { HelpLink } from './DirectorHelp.tsx';
import {
  RESTREAM_PLATFORMS,
  restreamPhaseLabel,
  restreamPlatform,
  type RestreamPlatform,
  type RestreamStatus,
  type RestreamTargetInput,
} from '@rabbitfuel/shared';
import { api } from '../lib/api.ts';

/**
 * Ausgang zu YouTube und Twitch.
 *
 * Der Zustand kommt vom Server und nicht aus diesem Panel -- deshalb steht
 * hier nach einem Reload sofort wieder "sendet, seit 14 Minuten", statt dass
 * die Regie raten muesste, ob der Stream noch laeuft. Gestartet wird per REST,
 * damit die Streamschluessel nicht durch dieselbe WebSocket gehen wie Chat
 * und Ticker; jede Zustandsaenderung kommt danach ueber den Raum zurueck.
 */
export function RestreamPanel({
  slug,
  pin,
  status,
}: {
  slug: string;
  pin: string | undefined;
  status: RestreamStatus;
}) {
  const [platform, setPlatform] = useState<RestreamPlatform>('youtube');
  const [streamKey, setStreamKey] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);

  // Ein Fehlerzustand ist kein laufender Ausgang -- da gehoert das Formular
  // hin, nicht der Stopp-Knopf. Die Meldung bleibt trotzdem stehen.
  const running = status.phase !== 'idle' && status.phase !== 'error';
  const info = restreamPlatform(platform);

  // Ob der Server ffmpeg/Chrome ueberhaupt anbietet, weiss nur der Server.
  useEffect(() => {
    api
      .getRestream(slug, pin)
      .then((r) => setEnabled(r.enabled))
      .catch(() => undefined);
  }, [slug, pin]);

  const start = async () => {
    setBusy(true);
    setError(null);
    const targets: RestreamTargetInput[] = [
      { platform, streamKey: streamKey.trim(), url: platform === 'custom' ? customUrl.trim() : undefined },
    ];
    try {
      await api.startRestream(slug, pin, targets);
      // Der Schluessel hat seinen Zweck erfuellt und muss nicht im Formular
      // stehen bleiben, wo ihn der naechste Blick auf den Bildschirm mitliest.
      setStreamKey('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Start fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.stopRestream(slug, pin);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Stopp fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="restream">
      <div className="restream__head">
        <span className={`tag tag--rtmp tag--rtmp-${status.phase}`}>
          {status.phase === 'live' && <span className="dot dot--pulse" />}
          RTMP · {restreamPhaseLabel[status.phase]}
        </span>
        <HelpLink topic="rtmp-ausgang" />
        <span className="stage-bar__sub">
          {running ? <RunningSummary status={status} /> : 'Hauptbild zusätzlich an eine Streaming-Plattform geben.'}
        </span>
      </div>

      {!enabled && (
        <div className="notice notice--stage">
          Dieser Server bietet keinen RTMP-Ausgang an (RESTREAM=0).
        </div>
      )}

      {status.message && (
        <div className="notice notice--stage">{status.message}</div>
      )}
      {error && <div className="notice notice--stage">{error}</div>}

      {running ? (
        <>
          <ul className="restream__targets">
            {status.targets.map((target) => (
              <li key={target.id}>
                <b>{restreamPlatform(target.platform).label}</b>
                <span>{target.url}</span>
                <span className="restream__key">Schlüssel {target.keyHint}</span>
              </li>
            ))}
          </ul>
          <button className="btn btn--sm btn--onstage" disabled={busy} onClick={() => void stop()}>
            Ausgang beenden
          </button>
          <p className="stage-bar__sub">
            Der Ausgang läuft auf dem Server. Du kannst diese Seite neu laden oder schließen — der Stream bei{' '}
            {status.targets.map((t) => restreamPlatform(t.platform).label).join(' und ')} läuft weiter.
          </p>
        </>
      ) : (
        <>
          <div className="chip-row">
            {RESTREAM_PLATFORMS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`chip${platform === p.id ? ' is-active' : ''}`}
                onClick={() => setPlatform(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {platform === 'custom' && (
            <input
              className="label-input"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="rtmp://mein-server/live"
              autoComplete="off"
            />
          )}
          <input
            className="label-input"
            type="password"
            value={streamKey}
            onChange={(e) => setStreamKey(e.target.value)}
            placeholder="Streamschlüssel"
            autoComplete="off"
          />
          <span className="stage-bar__sub">{info.hint}</span>
          <button
            className="btn btn--sm"
            disabled={busy || !enabled || !streamKey.trim() || (platform === 'custom' && !customUrl.trim())}
            onClick={() => void start()}
          >
            {busy ? 'startet …' : `Zu ${info.label} senden`}
          </button>
        </>
      )}
    </div>
  );
}

/** Laufzeit und uebertragene Menge -- beides zaehlt der Server, nicht der Tab. */
function RunningSummary({ status }: { status: RestreamStatus }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!status.startedAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [status.startedAt]);

  const seconds = status.startedAt ? Math.max(0, Math.floor((now - status.startedAt) / 1000)) : 0;
  const clock = useMemo(() => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const pad = (v: number) => String(v).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  }, [seconds]);

  return (
    <>
      {status.startedAt ? `seit ${clock}` : 'baut auf'}
      {status.bytesSent > 0 ? ` · ${formatBytes(status.bytesSent)} gesendet` : ''}
      {status.restarts > 0 ? ` · ${status.restarts}× neu aufgebaut` : ''}
      {status.startedBy ? ` · von ${status.startedBy}` : ''}
    </>
  );
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};
